// ./src/components/features/CreateEventWizard/steps/FeaturesStep.tsx
//
// "What else will you use?" — the event's features (ROADMAP A2b #62). The event already EXISTS here (it was created
// when the essentials were in), so what is ticked is saved to it on Next / Finish as `settings.features` — which is
// what the per-event menu (#63) reads.
//
// Everything this step shows is decided by `EventFeatureChecklist` (shared with the Event features settings page): the
// type's suggestions first, the rest under "More features", unavailable ones with the reason and the mode switch that
// would allow them, premium ones with the plans that include them.

import React, { useEffect } from 'react';
import { Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { useOptionalEventSetupCatalogContext } from '@/contexts';
import { useAppPlans } from '@/hooks';
import { WHOLE_SET } from '@/utils/api';
import { analytics, WIZARD_EVENTS, wizardFeatureParams } from '@/utils/analytics';
import { EventFeatureChecklist } from '@/components/features/EventFeatures/EventFeatureChecklist';

import { useEventWizard } from '../WizardContext';
import { EventMode, FEATURE_SETUP_STEP } from '../wizardTypes';

// The features that get their own step after this one — every `feature` in WIZARD_STEP_META.
const SETUP_NEXT = Object.keys(FEATURE_SETUP_STEP);

export const FeaturesStep: React.FC = () => {
  const { t } = useTranslation(['events', 'common']);
  const { draft, patchDraft, saving, switchEventMode } = useEventWizard();
  const catalog = useOptionalEventSetupCatalogContext();
  // The plans, for "Premium — included in Pro or Business". Loaded here, not app-wide: only this step and the
  // Subscription page need them.
  const { appPlans, list: listPlans } = useAppPlans({ autoLoad: false, pageSize: WHOLE_SET });

  useEffect(() => {
    listPlans({}, { page: 1, pageSize: WHOLE_SET }, undefined, true);
  }, [listPlans]);

  const type = catalog?.findType(draft.eventCategoryId) ?? null;

  return (
    <Stack gap="lg">
      <Text size="md" c="dimmed">
        {t('events:wizard.features.intro', {
          defaultValue:
            'Pick what this event will use — each one adds its pages to the event’s menu, and some get a step of their own next. You can change this any time from Overview › Event features.',
        })}
      </Text>
      <EventFeatureChecklist
        type={type}
        eventMode={draft.eventMode}
        value={draft.featuresOn ?? []}
        onChange={(next) => patchDraft({ featuresOn: next })}
        onSwitchMode={(mode) => void switchEventMode(mode as EventMode)}
        plans={appPlans}
        disabled={saving}
        setupNext={SETUP_NEXT}
        /* #62 P7 — the funnel's `wizard_feature`: what the host switched, and where its card sat. */
        onToggle={(feature, on, source) => analytics.sendEvent(WIZARD_EVENTS.FEATURE, wizardFeatureParams({ feature, on, source }))}
      />
    </Stack>
  );
};
